/*
 * =====================================================================================
 * DISCLAIMER:
 * =====================================================================================
 * This localization file is a community contribution and is not officially maintained
 * by Clerk. It has been provided by the community and may not be fully aligned
 * with the current or future states of the main application. Clerk does not guarantee
 * the accuracy, completeness, or timeliness of the translations in this file.
 * Use of this file is at your own risk and discretion.
 * =====================================================================================
 */

import type { LocalizationResource } from '@clerk/shared/types';

export const huHU: LocalizationResource = {
  locale: 'hu-HU',
  apiKeys: {
    action__add: 'Új kulcs hozzáadása',
    action__search: 'Kulcsok keresése',
    copySecret: {
      formButtonPrimary__copyAndClose: 'Másolás és bezárás',
      formHint: 'Biztonsági okokból később nem fogjuk tudni újra megengedni, hogy megtekintsd.',
      formTitle: 'Másold le az "{{name}}" API kulcsot most',
    },
    createdAndExpirationStatus__expiresOn:
      "Létrehozva: {{ createdDate | shortDate('hu-HU') }} • Lejár: {{ expiresDate | longDate('hu-HU') }}",
    createdAndExpirationStatus__never: "Létrehozva: {{ createdDate | shortDate('hu-HU') }} • Soha nem jár le",
    detailsTitle__emptyRow: 'Nem található API kulcs',
    formButtonPrimary__add: 'Kulcs létrehozása',
    formFieldCaption__expiration__expiresOn: 'Lejárat: {{ date }}',
    formFieldCaption__expiration__never: 'Ez a kulcs soha nem jár le',
    formFieldOption__expiration__180d: '180 nap',
    formFieldOption__expiration__1d: '1 nap',
    formFieldOption__expiration__1y: '1 év',
    formFieldOption__expiration__30d: '30 nap',
    formFieldOption__expiration__60d: '60 nap',
    formFieldOption__expiration__7d: '7 nap',
    formFieldOption__expiration__90d: '90 nap',
    formFieldOption__expiration__never: 'Soha',
    formHint: 'Adj meg egy nevet az új kulcs létrehozásához. Bármikor visszavonhatod.',
    formTitle: 'Új API kulcs hozzáadása',
    lastUsed__days: '{{days}} napja',
    lastUsed__hours: '{{hours}} órája',
    lastUsed__minutes: '{{minutes}} perce',
    lastUsed__months: '{{months}} hónapja',
    lastUsed__seconds: '{{seconds}} másodperce',
    lastUsed__years: '{{years}} éve',
    menuAction__revoke: 'Kulcs visszavonása',
    revokeConfirmation: {
      confirmationText: 'Visszavonás',
      formButtonPrimary__revoke: 'Kulcs visszavonása',
      formHint: 'Biztosan törölni szeretnéd ezt a titkos kulcsot?',
      formTitle: 'Visszavonod az "{{apiKeyName}}" titkos kulcsot?',
      inputLabel: undefined,
    },
    tableHeader__actions: undefined,
    tableHeader__lastUsed: undefined,
    tableHeader__name: undefined,
  },
  backButton: 'Vissza',
  badge__activePlan: 'Aktív',
  badge__banned: undefined,
  badge__canceledEndsAt: "Lemondva • Lejár: {{ date | shortDate('hu-HU') }}",
  badge__currentPlan: 'Jelenlegi csomag',
  badge__default: 'Alapértelmezett',
  badge__endsAt: "Lejár: {{ date | shortDate('hu-HU') }}",
  badge__expired: 'Lejárt',
  badge__freeTrial: 'Ingyenes próbaverzió',
  badge__otherImpersonatorDevice: 'Másik megszemélyesítő eszköz',
  badge__pastDueAt: "Lejárt: {{ date | shortDate('hu-HU') }}",
  badge__pastDuePlan: 'Lejárt',
  badge__primary: 'Elsődleges',
  badge__renewsAt: "Megújul: {{ date | shortDate('hu-HU') }}",
  badge__requiresAction: 'Beavatkozás szükséges',
  badge__startsAt: "Kezdődik: {{ date | shortDate('hu-HU') }}",
  badge__thisDevice: 'Ez az eszköz',
  badge__trialEndsAt: "Próbaverzió lejár: {{ date | shortDate('hu-HU') }}",
  badge__unverified: 'Nem ellenőrzött',
  badge__upcomingPlan: 'Közelgő',
  badge__userDevice: 'Felhasználói eszköz',
  badge__you: 'Te',
  billing: {
    accountCredit: 'Fiók egyenleg jóváírás',
    addPaymentMethod__label: 'Fizetési mód hozzáadása',
    alwaysFree: 'Mindig ingyenes',
    annually: 'Éves',
    availableFeatures: 'Elérhető funkciók',
    billedAnnually: 'Éves számlázás',
    billedAnnuallyOnly: 'Csak éves számlázás',
    billedMonthly: undefined,
    billedMonthlyOnly: 'Csak havi számlázás',
    cancelFreeTrial: 'Próbaverzió lemondása',
    cancelFreeTrialAccessUntil:
      "A próbaverziód {{ date | longDate('hu-HU') }}-ig marad aktív. Ezután elveszíted a próbaverziós funkciókat. Nem számítunk fel díjat.",
    cancelFreeTrialTitle: 'Lemondod a(z) {{plan}} csomag próbaverzióját?',
    cancelSubscription: 'Előfizetés lemondása',
    cancelSubscriptionAccessUntil:
      "A(z) '{{plan}}' funkcióit {{ date | longDate('hu-HU') }}-ig használhatod, ezután elveszíted a hozzáférést.",
    cancelSubscriptionNoCharge: 'Nem számítunk fel díjat ezért az előfizetésért.',
    cancelSubscriptionPastDue:
      'Az előfizetésed azonnal megszűnik, és elveszíted az összes csomagfunkciót. A következő előfizetéskor kérjük a lejárt összeg kifizetését.',
    cancelSubscriptionTitle: 'Lemondod a(z) {{plan}} előfizetést?',
    cannotSubscribeMonthly:
      'Erre a csomagra nem fizethetsz elő havi fizetéssel. Az előfizetéshez éves fizetést kell választanod.',
    cannotSubscribeUnrecoverable:
      'Nem tudsz előfizetni erre a csomagra. A jelenlegi előfizetésed drágább, mint ez a csomag.',
    checkout: {
      description__paymentSuccessful: 'A fizetés sikeres volt.',
      description__subscriptionSuccessful: 'Az új előfizetésed beállítva.',
      downgradeNotice:
        'A jelenlegi előfizetésed és funkciói a számlázási ciklus végéig megmaradnak, ezután átváltunk erre az előfizetésre.',
      emailForm: {
        subtitle: 'A vásárlás befejezése előtt meg kell adnod egy e-mail címet, ahová a nyugtákat küldjük.',
        title: 'E-mail cím hozzáadása',
      },
      lineItems: {
        title__freeTrialEndsAt: 'Próbaverzió lejárata',
        title__paymentMethod: 'Fizetési mód',
        title__statementId: 'Kivonat azonosító',
        title__subscriptionBegins: 'Előfizetés kezdete',
        title__totalPaid: 'Összesen fizetve',
      },
      pastDueNotice: 'Az előző előfizetésed lejárt, fizetés nélkül.',
      perMonth: 'havonta',
      title: 'Pénztár',
      title__paymentSuccessful: 'Sikeres fizetés!',
      title__subscriptionSuccessful: 'Sikeres!',
      title__trialSuccess: 'Próbaverzió sikeresen elindítva!',
      totalDueAfterTrial: 'Fizetendő összeg a próbaverzió lejárta után ({{days}} nap)',
      totalDuePerPeriod: undefined,
    },
    credit: 'Jóváírás',
    creditRemainder: 'Jóváírás a jelenlegi előfizetésed hátralévő idejére.',
    defaultFreePlanActive: 'Jelenleg az Ingyenes csomagot használod',
    free: 'Ingyenes',
    getStarted: 'Kezdés',
    highlightedPlanBadge: 'Népszerű',
    keepFreeTrial: 'Próbaverzió megtartása',
    keepSubscription: 'Előfizetés megtartása',
    manage: 'Kezelés',
    manageSubscription: 'Előfizetés kezelése',
    month: 'Hónap',
    monthAbbreviation: 'hó',
    monthPerUnit: 'Hónap / {{unitName}}',
    monthly: 'Havi',
    pastDue: 'Lejárt',
    pay: '{{amount}} fizetése',
    payerCreditRemainder: 'Jóváírás a fiók egyenlegből.',
    paymentMethod: {
      applePayDescription: {
        annual: 'Éves fizetés',
        monthly: 'Havi fizetés',
      },
      dev: {
        anyNumbers: 'Bármilyen számok',
        cardNumber: 'Kártyaszám',
        cvcZip: 'CVC, irányítószám',
        developmentMode: 'Fejlesztői mód',
        expirationDate: 'Lejárati dátum',
        testCardInfo: 'Teszt kártya adatok',
      },
    },
    paymentMethods__label: 'Fizetési módok',
    pricingTable: {
      billingCycle: 'Számlázási ciklus',
      included: 'Tartalmazza',
      seatCost: {
        additionalSeats: '({{additionalTierFeePerBlockAmount}}/{{periodAbbreviation}} további helyekért)',
        freeUpToSeats: '{{endsAfterBlock}} helyig ingyenes',
        includedSeats: '{{includedSeats}} hely tartalmazza',
        perSeat: '{{feePerBlockAmount}}/{{periodAbbreviation}} helyenként',
        tooltip: {
          additionalSeatsEach: 'További helyek {{feePerBlockAmount}}/{{period}} egyenként.',
          firstSeatsIncludedInPlan: 'Az első {{endsAfterBlock}} hely a csomag része.',
          freeForUpToSeats: '{{endsAfterBlock}} helyig ingyenes.',
        },
        unlimitedSeats: 'Korlátlan helyek',
        upToSeats: '{{endsAfterBlock}} helyig',
      },
    },
    proratedDiscount: undefined,
    prorationCredit: 'Arányosított jóváírás',
    reSubscribe: 'Újra előfizetés',
    seatBreakdownIncludedPlural: undefined,
    seatBreakdownIncludedSingular: undefined,
    seatBreakdownPlural: undefined,
    seatBreakdownSingular: undefined,
    seats: 'Helyek',
    seatsWithLimit: undefined,
    seeAllFeatures: 'Összes funkció megtekintése',
    startFreeTrial: 'Próbaverzió indítása',
    startFreeTrial__days: '{{days}} napos próbaverzió indítása',
    subscribe: 'Előfizetés',
    subscriptionDetails: {
      beginsOn: 'Kezdődik',
      currentBillingCycle: 'Jelenlegi számlázási ciklus',
      endsOn: 'Lejár',
      firstPaymentAmount: 'Első fizetés összege',
      firstPaymentOn: 'Első fizetés dátuma',
      nextPaymentAmount: 'Következő fizetés összege',
      nextPaymentOn: 'Következő fizetés dátuma',
      pastDueAt: 'Lejárt',
      renewsAt: 'Megújul',
      subscribedOn: 'Előfizetve',
      title: 'Előfizetés',
      trialEndsOn: 'Próbaverzió lejárata',
      trialStartedOn: 'Próbaverzió kezdete',
    },
    subtotal: 'Részösszeg',
    subtotalRenewal: undefined,
    switchPlan: 'Váltás erre a csomagra',
    switchToAnnual: 'Váltás éves fizetésre',
    switchToAnnualWithAnnualPrice: 'Váltás éves fizetésre: {{price}} / év',
    switchToMonthly: 'Váltás havi fizetésre',
    switchToMonthlyWithPrice: 'Váltás havi fizetésre: {{price}} / hó',
    totalDue: 'Fizetendő összeg',
    totalDuePerPeriod: undefined,
    totalDueToday: 'Mai fizetendő összeg',
    viewFeatures: 'Funkciók megtekintése',
    viewPayment: 'Fizetés megtekintése',
    year: 'Év',
    yearAbbreviation: 'év',
    yearPerUnit: 'Év / {{unitName}}',
  },
  configureSSO: {
    configureStep: {
      attributeMappingTable: {
        badges: {
          optional: undefined,
          required: undefined,
        },
      },
      samlCustom: {
        assignUsersStep: {
          headerSubtitle: undefined,
          paragraph: undefined,
          title: undefined,
        },
        attributeMappingStep: {
          attributeMappingTable: {
            columns: {
              attributeName: undefined,
              userProfile: undefined,
            },
            rows: {
              email: {
                attributeName: undefined,
                userProfile: undefined,
              },
              firstName: {
                attributeName: undefined,
                userProfile: undefined,
              },
              lastName: {
                attributeName: undefined,
                userProfile: undefined,
              },
            },
          },
          headerSubtitle: undefined,
          paragraph: undefined,
        },
        createAppStep: {
          createAppInstructions: {
            paragraph: undefined,
            title: undefined,
          },
          headerSubtitle: undefined,
          serviceProviderFields: {
            acsUrl: {
              label: undefined,
            },
            spEntityId: {
              label: undefined,
            },
          },
        },
        identityProviderMetadataStep: {
          headerSubtitle: undefined,
          manual: {
            description: undefined,
            issuer: {
              label: undefined,
              placeholder: undefined,
            },
            signOnUrl: {
              label: undefined,
              placeholder: undefined,
            },
            signingCertificate: {
              fileUploaded: undefined,
              label: undefined,
              removeFile: undefined,
              replaceFile: undefined,
              uploadFile: undefined,
            },
          },
          metadataUrl: {
            description: undefined,
            label: undefined,
            placeholder: undefined,
          },
          modes: {
            ariaLabel: undefined,
            manual: undefined,
            metadataUrl: undefined,
            title: undefined,
          },
        },
        mainHeaderTitle: undefined,
      },
      samlGoogle: {
        attributeMappingStep: {
          attributeMappingTable: {
            columns: {
              appAttribute: undefined,
              googleAttribute: undefined,
            },
            rows: {
              email: {
                appAttribute: undefined,
                googleAttribute: undefined,
              },
              firstName: {
                appAttribute: undefined,
                googleAttribute: undefined,
              },
              lastName: {
                appAttribute: undefined,
                googleAttribute: undefined,
              },
            },
          },
          headerSubtitle: undefined,
          paragraph: undefined,
          step1: undefined,
          step2: undefined,
        },
        configureUserAccess: {
          assignUsersInstructions: {
            paragraph1: undefined,
            paragraph2: undefined,
            step1: undefined,
            step2: undefined,
            step3: undefined,
          },
          headerSubtitle: undefined,
        },
        createAppStep: {
          createAppInstructions: {
            step1: undefined,
            step2: undefined,
            step3: undefined,
            step4: undefined,
            step5: undefined,
            title: undefined,
          },
          headerSubtitle: undefined,
        },
        identityProviderMetadataStep: {
          headerSubtitle: undefined,
          manual: {
            description: undefined,
            issuer: {
              label: undefined,
              placeholder: undefined,
            },
            signOnUrl: {
              label: undefined,
              placeholder: undefined,
            },
            signingCertificate: {
              fileUploaded: undefined,
              label: undefined,
              removeFile: undefined,
              replaceFile: undefined,
              uploadFile: undefined,
            },
          },
          metadataFile: {
            description: undefined,
            fileUploaded: undefined,
            label: undefined,
            removeFile: undefined,
            replaceFile: undefined,
            uploadFile: undefined,
          },
          modes: {
            ariaLabel: undefined,
            manual: undefined,
            metadataFile: undefined,
            title: undefined,
          },
        },
        mainHeaderTitle: undefined,
        serviceProviderStep: {
          headerSubtitle: undefined,
          nameIdInstructions: {
            step1: undefined,
            step2: undefined,
          },
          paragraph: undefined,
          serviceProviderFields: {
            acsUrl: {
              label: undefined,
            },
            spEntityId: {
              label: undefined,
            },
          },
          title: undefined,
        },
      },
      samlMicrosoft: {
        attributeMappingStep: {
          attributeMappingTable: {
            columns: {
              attribute: undefined,
              claimName: undefined,
              value: undefined,
            },
            rows: {
              email: {
                attribute: undefined,
                claimName: undefined,
                value: undefined,
              },
              firstName: {
                attribute: undefined,
                claimName: undefined,
                value: undefined,
              },
              lastName: {
                attribute: undefined,
                claimName: undefined,
                value: undefined,
              },
            },
          },
          headerSubtitle: undefined,
          paragraph: undefined,
          step1: undefined,
          step2: undefined,
          step3: undefined,
          title: undefined,
        },
        createAppStep: {
          assignUsersInstructions: {
            paragraph1: undefined,
            step1: undefined,
            step2: undefined,
            step3: undefined,
            step4: undefined,
            step5: undefined,
            step6: undefined,
            title: undefined,
          },
          createAppInstructions: {
            step1: undefined,
            step2: undefined,
            step3: undefined,
            step4: {
              label: undefined,
              subSteps: {
                appName: undefined,
                create: undefined,
                nonGallery: undefined,
              },
            },
            title: undefined,
          },
          headerSubtitle: undefined,
        },
        identityProviderMetadataStep: {
          headerSubtitle: undefined,
          manual: {
            description: undefined,
            issuer: {
              label: undefined,
              placeholder: undefined,
            },
            signOnUrl: {
              label: undefined,
              placeholder: undefined,
            },
            signingCertificate: {
              fileUploaded: undefined,
              label: undefined,
              removeFile: undefined,
              replaceFile: undefined,
              uploadFile: undefined,
            },
          },
          metadataUrl: {
            description: undefined,
            label: undefined,
            placeholder: undefined,
          },
          modes: {
            ariaLabel: undefined,
            manual: undefined,
            metadataUrl: undefined,
            title: undefined,
          },
        },
        mainHeaderTitle: undefined,
        serviceProviderStep: {
          headerSubtitle: undefined,
          serviceProviderFields: {
            acsUrl: {
              label: undefined,
            },
            spEntityId: {
              label: undefined,
            },
          },
          step1: undefined,
          step2: undefined,
          step3: undefined,
          step4: undefined,
          step5: undefined,
          step6: undefined,
          title: undefined,
        },
      },
      samlOkta: {
        assignUsersStep: {
          assignUsersInstructions: {
            paragraph: undefined,
            step1: undefined,
            step2: undefined,
            step3: undefined,
            step4: undefined,
            step5: undefined,
            title: undefined,
          },
          headerSubtitle: undefined,
        },
        attributeMappingStep: {
          attributeMappingTable: {
            columns: {
              expression: undefined,
              name: undefined,
            },
            rows: {
              email: {
                expression: undefined,
                name: undefined,
              },
              firstName: {
                expression: undefined,
                name: undefined,
              },
              lastName: {
                expression: undefined,
                name: undefined,
              },
            },
          },
          headerSubtitle: undefined,
          paragraph: undefined,
          step1: undefined,
          step2: undefined,
        },
        createAppStep: {
          completeSamlIntegrationInstructions: {
            step1: undefined,
            step2: undefined,
            title: undefined,
          },
          createAppInstructions: {
            step1: undefined,
            step2: undefined,
            step3: undefined,
            step4: undefined,
            step5: undefined,
            title: undefined,
          },
          headerSubtitle: undefined,
          serviceProviderInstructions: {
            paragraph1: undefined,
            paragraph2: undefined,
            serviceProviderFields: {
              acsUrl: {
                label: undefined,
              },
              spEntityId: {
                label: undefined,
              },
            },
            title: undefined,
          },
        },
        identityProviderMetadataStep: {
          headerSubtitle: undefined,
          manual: {
            description: undefined,
            issuer: {
              label: undefined,
              placeholder: undefined,
            },
            signOnUrl: {
              label: undefined,
              placeholder: undefined,
            },
            signingCertificate: {
              fileUploaded: undefined,
              label: undefined,
              removeFile: undefined,
              replaceFile: undefined,
              uploadFile: undefined,
            },
          },
          metadataUrl: {
            description: undefined,
            label: undefined,
            placeholder: undefined,
          },
          modes: {
            ariaLabel: undefined,
            manual: undefined,
            metadataUrl: undefined,
            title: undefined,
          },
        },
        mainHeaderTitle: undefined,
      },
    },
    confirmation: {
      configurationSection: {
        configureAgainLink: undefined,
        issuerLabel: undefined,
        ssoUrlLabel: undefined,
        title: undefined,
      },
      domainSection: {
        title: undefined,
      },
      enableSection: {
        title: undefined,
      },
      inactiveBanner: {
        title: undefined,
      },
      resetSection: {
        confirmationFieldLabel: undefined,
        submitButton: undefined,
        title: undefined,
        warning: undefined,
      },
      statusSection: {
        activeBadge: undefined,
        inactiveBadge: undefined,
        title: undefined,
      },
    },
    missingManageEnterpriseConnectionsPermission: {
      subtitle: 'Vegye fel a kapcsolatot szervezete adminisztrátorával a jogosultságai bővítéséhez.',
      title: 'Nincs jogosultsága az egyszeri bejelentkezés (SSO) kezelésére',
    },
    navbar: {
      title: 'Egyszeri bejelentkezés (SSO) beállítása',
    },
    organizationDomainsStep: {
      domainCard: {
        badge__unverified: 'Nincs igazolva',
        badge__verified: 'Igazolva',
        removeButtonTooltip__lastVerifiedDomain: undefined,
        removeButtonTooltip__lastVerifiedDomainActive: undefined,
        txtRecord: {
          hostLabel: 'Gazda / Név',
          instructions:
            'Adja hozzá ezt a TXT-rekordot a DNS-szolgáltatójához. Automatikusan igazoljuk, amint a rekord aktívvá válik.',
          typeLabel: 'Típus',
          valueLabel: 'Érték',
        },
        verifiedAtLabel: "Igazolva: {{ date | shortDate('hu-HU') }}",
      },
      domainSuggestion: {
        formButtonPrimary__add: '{{domain}} hozzáadása',
        messageLabel: 'Az e-mail-címe a következőt használja: {{domain}}. Szeretné hozzáadni?',
      },
      formButtonPrimary__add: 'Hozzáadás',
      formFieldInputPlaceholder__domain: 'Tartomány hozzáadása',
      formFieldLabel__domain: 'Tartomány',
      removeDomainDialog: {
        cancelButton: undefined,
        removeButton: undefined,
        subtitle__active: undefined,
        subtitle__inactive: undefined,
        title: undefined,
      },
      subtitle:
        'Adja hozzá és igazolja azon tartományok tulajdonjogát, amelyeket szervezete a bejelentkezéshez használ.',
      title: 'SSO-tartományok hozzáadása',
    },
    resetConnectionDialog: {
      cancelButton: undefined,
      confirmationFieldLabel: undefined,
      confirmationFieldPlaceholder: undefined,
      resetButton: undefined,
      subtitle: undefined,
      title: undefined,
    },
    selectProviderStep: {
      saml: {
        customSaml: 'Egyéni SAML szolgáltató',
        google: undefined,
        groupLabel: 'SAML',
        microsoft: undefined,
        okta: 'Okta Workforce',
      },
      subtitle: 'Válassza ki azt a szolgáltatót, amelyhez beállítja az SSO-t.',
      title: 'Szolgáltató kiválasztása',
      warning: 'Miután kiválasztotta a szolgáltatót, nem módosíthatja, amíg a konfiguráció be nem fejeződik',
    },
    testConfigurationStep: {
      error__noSuccessfulTestRun: undefined,
      subtitle: undefined,
      testResults: {
        actionLabel__refresh: undefined,
        empty: {
          subtitle: undefined,
          title: undefined,
        },
        polling: undefined,
        status__failed: undefined,
        status__pending: undefined,
        status__success: undefined,
        title: undefined,
      },
      testRunDetails: {
        howToFix: {
          actionLabel__viewDocumentation: undefined,
          oauth_access_denied: {
            description: undefined,
          },
          oauth_fetch_user_error: {
            intro: undefined,
            step1: undefined,
            step2: undefined,
          },
          oauth_token_exchange_error: {
            description: undefined,
          },
          saml_email_address_domain_mismatch: {
            description: undefined,
          },
          saml_response_relaystate_missing: {
            description: undefined,
          },
          saml_user_attribute_missing: {
            intro: undefined,
            step1: undefined,
            step2: undefined,
            step3: undefined,
          },
          sectionTitle: undefined,
        },
        parsedUserInfo: {
          email: undefined,
          firstName: undefined,
          sectionTitle: undefined,
        },
        runDetails: {
          actionLabel__copied: undefined,
          actionLabel__copy: undefined,
          errorCode: undefined,
          fullMessage: undefined,
          sectionTitle: undefined,
          status: undefined,
          timestamp: undefined,
        },
        title: undefined,
      },
      testUrl: {
        actionLabel__open: undefined,
      },
      title: undefined,
    },
  },
  createOrganization: {
    formButtonSubmit: 'Szervezet létrehozása',
    invitePage: {
      formButtonReset: 'Kihagyás',
    },
    title: 'Szervezet létrehozása',
  },
  dates: {
    lastDay: "Tegnap {{ date | timeString('hu-HU') }}-kor",
    next6Days: "{{ date | weekday('hu-HU','long') }} {{ date | timeString('hu-HU') }}-kor",
    nextDay: "Holnap {{ date | timeString('hu-HU') }}-kor",
    numeric: "{{ date | timeString('hu-HU') }}",
    previous6Days: "Elmúlt {{ date | weekday('hu-HU','long') }} {{ date | timeString('hu-HU') }}-kor",
    sameDay: "Ma {{ date | timeString('hu-HU') }}-kor",
  },
  dividerText: 'vagy',
  footerActionLink__alternativePhoneCodeProvider: 'Kód küldése SMS-ben',
  footerActionLink__useAnotherMethod: 'Másik módszer használata',
  footerPageLink__help: 'Súgó',
  footerPageLink__privacy: 'Adatvédelem',
  footerPageLink__terms: 'Felhasználási feltételek',
  formButtonPrimary: 'Tovább',
  formButtonPrimary__verify: 'Ellenőrzés',
  formFieldAction__forgotPassword: 'Elfelejtetted a jelszavad?',
  formFieldError__matchingPasswords: 'A jelszavak megegyeznek',
  formFieldError__notMatchingPasswords: 'A jelszavak nem egyeznek',
  formFieldError__verificationLinkExpired: 'A megerősítő link lejárt. Kérlek kérj egy újat.',
  formFieldHintText__optional: 'Nem kötelező',
  formFieldHintText__slug: 'A slug egy egyedi azonosító, amelyet általában URL-ben használunk.',
  formFieldInputPlaceholder__apiKeyDescription: 'Írd le, miért hozod létre ezt a kulcsot',
  formFieldInputPlaceholder__apiKeyExpirationDate: 'Válassz dátumot',
  formFieldInputPlaceholder__apiKeyName: 'Add meg a titkos kulcs nevét',
  formFieldInputPlaceholder__backupCode: 'Írd be a tartalék kódot',
  formFieldInputPlaceholder__confirmDeletionUserAccount: 'Fiók törlése',
  formFieldInputPlaceholder__emailAddress: 'Add meg az e-mail címedet',
  formFieldInputPlaceholder__emailAddress_username: 'Add meg az e-mail címedet vagy felhasználónevedet',
  formFieldInputPlaceholder__emailAddresses: 'pelda@email.hu, pelda2@email.hu',
  formFieldInputPlaceholder__firstName: 'Keresztnév',
  formFieldInputPlaceholder__lastName: 'Vezetéknév',
  formFieldInputPlaceholder__organizationDomain: 'pelda.hu',
  formFieldInputPlaceholder__organizationDomainEmailAddress: 'te@pelda.hu',
  formFieldInputPlaceholder__organizationName: 'Szervezet neve',
  formFieldInputPlaceholder__organizationSlug: 'my-org',
  formFieldInputPlaceholder__password: 'Add meg a jelszavad',
  formFieldInputPlaceholder__phoneNumber: 'Add meg a telefonszámod',
  formFieldInputPlaceholder__signUpPassword: 'Hozz létre egy jelszót',
  formFieldInputPlaceholder__username: 'Add meg a felhasználóneved',
  formFieldInput__emailAddress_format: 'Példa formátum: nev@pelda.hu',
  formFieldLabel__apiKey: 'API kulcs',
  formFieldLabel__apiKeyDescription: 'Leírás',
  formFieldLabel__apiKeyExpiration: 'Lejárat',
  formFieldLabel__apiKeyName: 'API kulcs neve',
  formFieldLabel__automaticInvitations: 'Automatikus meghívások engedélyezése ezen a domainen',
  formFieldLabel__backupCode: 'Tartalék kód',
  formFieldLabel__confirmDeletion: 'Megerősítés',
  formFieldLabel__confirmPassword: 'Jelszó megerősítése',
  formFieldLabel__currentPassword: 'Jelenlegi jelszó',
  formFieldLabel__emailAddress: 'Email cím',
  formFieldLabel__emailAddress_username: 'Email cím vagy felhasználónév',
  formFieldLabel__emailAddresses: 'Email címek',
  formFieldLabel__firstName: 'Keresztnév',
  formFieldLabel__lastName: 'Vezetéknév',
  formFieldLabel__newPassword: 'Új jelszó',
  formFieldLabel__organizationDomain: 'Domain',
  formFieldLabel__organizationDomainDeletePending: 'Függőben lévő meghívások és javaslatok törlése',
  formFieldLabel__organizationDomainEmailAddress: 'Visszaigazoló email cím',
  formFieldLabel__organizationDomainEmailAddressDescription:
    'Írj be egy email címet ez alatt a domain alatt, hogy visszaigazold a domaint.',
  formFieldLabel__organizationName: 'Szervezet neve',
  formFieldLabel__organizationSlug: 'Slug',
  formFieldLabel__passkeyName: 'Passkey neve',
  formFieldLabel__password: 'Jelszó',
  formFieldLabel__phoneNumber: 'Telefonszám',
  formFieldLabel__role: 'Beosztás',
  formFieldLabel__signOutOfOtherSessions: 'Kijelentkeztetés minden más eszközökről',
  formFieldLabel__username: 'Felhasználónév',
  identityPreviewEditButton__emailAddress: undefined,
  identityPreviewEditButton__identifier: undefined,
  identityPreviewEditButton__phoneNumber: undefined,
  impersonationFab: {
    action__signOut: 'Kijelentkezés',
    title: 'Bejelntkezve mint {{identifier}}',
  },
  lastAuthenticationStrategy: 'Legutóbb használt',
  maintenanceMode: 'Jelenleg karbantartás alatt állunk, de ne aggódj, ez nem tart tovább pár percnél!',
  membershipRole__admin: 'Adminisztrátor',
  membershipRole__basicMember: 'Tag',
  membershipRole__guestMember: 'Vendég',
  oauthConsent: {
    action__allow: undefined,
    action__deny: undefined,
    offlineAccessNotice: undefined,
    redirectNotice: undefined,
    redirectUriModal: {
      subtitle: undefined,
      title: undefined,
    },
    scopeList: {
      title: undefined,
    },
    subtitle: undefined,
    viewFullUrl: undefined,
    warning: undefined,
  },
  organizationList: {
    action__createOrganization: 'Szervezet létrehozása',
    action__invitationAccept: 'Csatlakozás',
    action__suggestionsAccept: 'Csatlakozás kérése',
    createOrganization: 'Szervezet létrehozása',
    invitationAcceptedLabel: 'Meghívás elfogadva',
    subtitle: 'Amivel folytathatod a(z) {{applicationName}}',
    suggestionsAcceptedLabel: 'Elfogadásra vár',
    title: 'Válassz egy fiókot',
    titleWithoutPersonal: 'Válassz egy szervezetet',
  },
  organizationProfile: {
    apiKeysPage: {
      title: 'API kulcsok',
    },
    badge__automaticInvitation: 'Automatikus meghívások',
    badge__automaticSuggestion: 'Automatikus javaslatok',
    badge__enterpriseSso: undefined,
    badge__manualInvitation: 'Nincs automatikus felvétel',
    badge__unverified: 'Nincs visszaigazolva',
    billingPage: {
      paymentHistorySection: {
        empty: 'Nincs fizetési előzmény',
        notFound: 'A fizetési kísérlet nem található',
        tableHeader__amount: 'Összeg',
        tableHeader__date: 'Dátum',
        tableHeader__status: 'Állapot',
      },
      paymentMethodsSection: {
        actionLabel__default: 'Beállítás alapértelmezettként',
        actionLabel__remove: 'Eltávolítás',
        add: 'Új fizetési mód hozzáadása',
        addSubtitle: 'Adj hozzá egy új fizetési módot a fiókodhoz.',
        cancelButton: 'Mégse',
        formButtonPrimary__add: 'Fizetési mód hozzáadása',
        formButtonPrimary__pay: '{{amount}} fizetése',
        payWithTestCardButton: 'Fizetés teszt kártyával',
        removeMethod: {
          messageLine1: '{{identifier}} eltávolításra kerül erről a fiókról.',
          messageLine2:
            'Nem fogod tudni használni ezt a fizetési módot, és a hozzá kapcsolódó ismétlődő előfizetések nem fognak működni.',
          successMessage: '{{paymentMethod}} eltávolítva a fiókodról.',
          title: 'Fizetési mód eltávolítása',
        },
        title: 'Fizetési módok',
      },
      start: {
        headerTitle__payments: 'Fizetések',
        headerTitle__plans: 'Csomagok',
        headerTitle__statements: 'Kivonatok',
        headerTitle__subscriptions: 'Előfizetés',
      },
      statementsSection: {
        empty: 'Nincsenek megjeleníthető kivonatok',
        itemCaption__paidForPlan: 'Fizetve a(z) {{plan}} {{period}} csomagért',
        itemCaption__payerCredit: 'Jóváírás a fiók egyenlegből',
        itemCaption__proratedCredit: 'Arányosított jóváírás az előző előfizetés részleges használatáért',
        itemCaption__subscribedAndPaidForPlan: 'Előfizetve és fizetve a(z) {{plan}} {{period}} csomagért',
        notFound: 'Kivonat nem található',
        tableHeader__amount: 'Összeg',
        tableHeader__date: 'Dátum',
        title: 'Kivonatok',
        totalPaid: 'Összesen fizetve',
      },
      subscriptionsListSection: {
        actionLabel__manageSubscription: 'Kezelés',
        actionLabel__newSubscription: 'Előfizetés csomagra',
        actionLabel__switchPlan: 'Csomag váltása',
        includedSeatsUsage: undefined,
        overview: undefined,
        paidSeatsUsage: undefined,
        seatLimit: undefined,
        seatLimitAndIncludedSeats: undefined,
        tableHeader__edit: 'Szerkesztés',
        tableHeader__plan: 'Csomag',
        tableHeader__startDate: 'Kezdő dátum',
        title: 'Előfizetés',
      },
      subscriptionsSection: {
        actionLabel__default: 'Kezelés',
      },
      switchPlansSection: {
        title: 'Csomag váltása',
      },
      title: 'Számlázás',
    },
    createDomainPage: {
      subtitle:
        'Add meg a visszaigazolandó domain nevét. Minden email cím erről a tartományjól automatikusan tud csatlakozni a szervezethez.',
      title: 'Domain hozzáadása',
    },
    invitePage: {
      detailsTitle__inviteFailed:
        'A meghívok küldése sikertelen. Már vannak függőben lévő meghívók a következő címekre: {{email_addresses}}',
      formButtonPrimary__continue: 'Meghívók küldése',
      formButtonPrimary__purchaseSeats: undefined,
      selectDropdown__role: 'Válassz beosztást',
      subtitle: 'Írj be vagy illessz be egy vagy több email címet, vesszővel, vagy szóközzel elválasztva.',
      successMessage: 'A meghívók sikeresen elküldve',
      title: 'Új tagok meghívása',
    },
    membersPage: {
      action__invite: 'Meghívás',
      action__search: 'Keresés',
      activeMembersTab: {
        menuAction__remove: 'Tag eltávolítása',
        tableHeader__actions: 'Műveletek',
        tableHeader__joined: 'Csatlakozott',
        tableHeader__role: 'Beosztás',
        tableHeader__user: 'Felhasználó',
      },
      alerts: {
        roleSetMigrationInProgress: {
          subtitle: 'Frissítjük az elérhető szerepköröket. Ha ez befejeződött, újra frissítheti a szerepköröket.',
          title: 'A szerepkörök ideiglenesen zárolva vannak',
        },
      },
      detailsTitle__emptyRow: 'Nincsenek listázható tagok',
      invitationsTab: {
        autoInvitations: {
          headerSubtitle:
            'Adj meg egy email domaint, hogy meghívhass tagokat. Bárki aki erről a tartományról regisztrál, bármikor, csatlakozhat a szervezethez bármikor',
          headerTitle: 'Automatikus meghívások',
          primaryButton: 'Visszaigazolt domainek kezelése',
        },
        table__emptyRow: 'Nincsenek listázható meghívások',
      },
      invitedMembersTab: {
        menuAction__revoke: 'Meghívó visszavonása',
        tableHeader__invited: 'Meghívva',
      },
      requestsTab: {
        autoSuggestions: {
          headerSubtitle:
            'Azok a felhasználók, akik, egyező email domainnel regisztrálnak, látni fognak egy javaslatot, hogy csatlakozzanak a szervezetedhez.',
          headerTitle: 'Automatikus javaslat',
          primaryButton: 'Visszaigazolt domainek kezelése',
        },
        menuAction__approve: 'Elfogadás',
        menuAction__reject: 'Elutasítás',
        tableHeader__requested: 'Hozzáférés kérése',
        table__emptyRow: 'Nincsenek listázható kérések',
      },
      start: {
        headerTitle__invitations: 'Meghívók',
        headerTitle__members: 'Tagok',
        headerTitle__requests: 'Kérések',
      },
    },
    navbar: {
      apiKeys: 'API kulcsok',
      billing: 'Számlázás',
      description: 'A szervezeted kezelése',
      general: 'Általános',
      members: 'Tagok',
      security: undefined,
      title: 'Szervezet',
    },
    plansPage: {
      alerts: {
        noPermissionsToManageBilling: 'Nincs jogosultságod a szervezet számlázásának kezeléséhez.',
        planMembershipLimitExceeded:
          'A szervezetednek {{count}} tagja van (beleértve a függőben lévő meghívásokat). Ez a csomag csak {{limit}} tagot engedélyez.',
      },
      title: 'Csomagok',
    },
    profilePage: {
      dangerSection: {
        deleteOrganization: {
          actionDescription: 'Írd be, hogy: "{{organizationName}}" a folytatáshoz',
          messageLine1: 'Biztosan törölni szeretnéd ez a szervezetet?',
          messageLine2: 'Ez a művelet végleges és visszafordíthatatlan.',
          successMessage: 'Kitörölted a szervezetet.',
          title: 'Szervezet törlése',
        },
        leaveOrganization: {
          actionDescription: 'Írd be, hogy: "{{organizationName}}" a folytatáshoz',
          messageLine1:
            'Biztos vagy benne, hogy el szeretnéd hagyni a szervezetet? Elveszíted a hozzáférést a szervezethez, és alkamazásaihoz.',
          messageLine2: 'Ez a művelet végleges és visszafordíthatatlan.',
          successMessage: 'Elhagytad a szervezetet.',
          title: 'Szervezet elhagyása',
        },
        title: 'Veszély',
      },
      domainSection: {
        menuAction__manage: 'Kezelés',
        menuAction__remove: 'Törlés',
        menuAction__verify: 'Visszaigazolás',
        primaryButton: 'Domain hozzáadása',
        subtitle:
          'Endegélyezd, hogy a felhasználók automatikusan csatlakozhassanak a szervezetedhez, vagy hozzáférést kérjenek, az email domainjük alapján.',
        title: 'Visszaigazolt domainek',
      },
      successMessage: 'A szervezet frissítve',
      title: 'Profil frissítése',
    },
    removeDomainPage: {
      messageLine1: 'Az email domain {{domain}} el lesz távolítva',
      messageLine2: 'Ez után a felhasználók nem tudnak automatikusan csatlakozni',
      successMessage: '{{domain}} törölve lett',
      title: 'Domain törlése',
    },
    securityPage: {
      removeDialog: {
        confirmButton: undefined,
        subtitle: undefined,
        title: undefined,
      },
      ssoSection: {
        badge__active: undefined,
        badge__inProgress: undefined,
        badge__inactive: undefined,
        badge__unconfigured: undefined,
        descriptionLine1: undefined,
        domainLabel: undefined,
        menuAction__activate: undefined,
        menuAction__deactivate: undefined,
        menuAction__edit: undefined,
        menuAction__remove: undefined,
        primaryButton__continueConfiguration: undefined,
        primaryButton__startConfiguration: undefined,
        title: undefined,
      },
      title: undefined,
    },
    start: {
      headerTitle__general: 'Általános',
      headerTitle__members: 'Tagok',
      membershipSeatUsageLabel: '{{count}} / {{limit}} hely használatban',
      profileSection: {
        primaryButton: 'Profil frissítése',
        title: 'Szervezet Profil',
        uploadAction__title: 'Logo',
      },
    },
    verifiedDomainPage: {
      dangerTab: {
        calloutInfoLabel: 'A domain törlése befolyásolja a meghívott felhasználókat',
        removeDomainActionLabel__remove: 'Domain törlése',
        removeDomainSubtitle: 'Domain törlése a visszaigazolt domainek listájáról',
        removeDomainTitle: 'Domain törlése',
      },
      enrollmentTab: {
        automaticInvitationOption__description:
          'A felhasználók automatikusan meg lesznek hívva a szervezetbe, amikor regisztrálnak, és bármikor csatlakozhatnak.',
        automaticInvitationOption__label: 'Automatikus meghívások',
        automaticSuggestionOption__description:
          'A felasználók kapnak egy javaslatot, hogy kérjenek hozzáférést, de előbb egy adminisztrátornak jóvá kell hagynia, mielőtt csatlakozhatnak.',
        automaticSuggestionOption__label: 'Automatikus javaslatok',
        calloutInfoLabel: 'A felvételi mód megváltoztatása csak az új felhasználókra lesz hatással',
        calloutInvitationCountLabel: 'Függőben lévő meghívók: {{count}}',
        calloutSuggestionCountLabel: 'Függőben lévő javaslatok: {{count}}',
        manualInvitationOption__description: 'Felhasználókat csak manuálisan lehet meghívni a szervezetbe.',
        manualInvitationOption__label: 'Nincs automatikus felvétel',
        subtitle: 'Válaszd ki, hogy a felhasználók, hogyan csatlakozhatnak szervezethez.',
      },
      start: {
        headerTitle__danger: 'Veszély',
        headerTitle__enrollment: 'Felvételi opciók',
      },
      subtitle: 'A domain {{domain}} visszaigazolva. A folytatáshoz válassz felvételi módot',
      title: '{{domain}} frissítése',
    },
    verifyDomainPage: {
      formSubtitle: 'Írd be a megerősítő kódot, amit elküldtünk az email címedre',
      formTitle: 'Megerősítő kód',
      resendButton: 'Nem kaptál kódot? Újraküldés',
      subtitle: 'A domain {{domainName}}-t emaillel jóvá kell hagyni.',
      subtitleVerificationCodeScreen:
        'Elküldtük a megerősítő kódot a(z) {{emailAddress}} címre. A folytatáshoz írd be a kódot',
      title: 'Domain visszaigazolása',
    },
  },
  organizationSwitcher: {
    action__closeOrganizationSwitcher: 'Szervezetváltó bezárása',
    action__createOrganization: 'Szervezet létrehozása',
    action__invitationAccept: 'Csatlakozás',
    action__manageOrganization: 'Kezelés',
    action__openOrganizationSwitcher: 'Szervezetváltó megnyitása',
    action__suggestionsAccept: 'Csatlakozás kérése',
    notSelected: 'Nincs szervezet kiválasztva',
    personalWorkspace: 'Személyes fiók',
    suggestionsAcceptedLabel: 'Elfogadásra vár',
  },
  paginationButton__next: 'Következő',
  paginationButton__previous: 'Előző',
  paginationRowText__displaying: 'Mutat',
  paginationRowText__of: '-ból/-ből',
  reverification: {
    alternativeMethods: {
      actionLink: 'Segítség kérése',
      actionText: 'Nincs ezekből egyik sem?',
      blockButton__backupCode: 'Tartalék kód használata',
      blockButton__emailCode: 'Email kód küldése: {{identifier}}',
      blockButton__passkey: 'Passkey használata',
      blockButton__password: 'Folytatás jelszóval',
      blockButton__phoneCode: 'SMS kód küldése: {{identifier}}',
      blockButton__totp: 'Hitelesítő alkalmazás használata',
      getHelp: {
        blockButton__emailSupport: 'Segítség kérése emailben',
        content:
          'Ha problémád van a fiókodat ellenőrzéssel, írj nekünk emailt, és segítünk a hozzáférés visszaállításában, amilyen gyorsan csak lehet.',
        title: 'Segítség kérése',
      },
      subtitle: 'Problémád akadt? Ezek közül bármelyik módszert választhatod az ellenőrzéshez.',
      title: 'Másik módszer használata',
    },
    backupCodeMfa: {
      subtitle: 'Írd be a tartalék kódot, amit a kétlépcsős azonosítás beállításakor kaptál',
      title: 'Írd be a tartalék kódot',
    },
    emailCode: {
      formTitle: 'Visszaigazoló kód',
      resendButton: 'Nem kaptad meg a kódot? Újraküldés',
      subtitle: 'Írd be az e-mail címedre küldött kódot a folytatáshoz',
      title: 'Ellenőrzés szükséges',
    },
    noAvailableMethods: {
      message: 'Nem lehet folytatni az ellenőrzést. Nincs konfigurált hitelesítési módszer.',
      subtitle: 'Hiba történt',
      title: 'Nem sikerült ellenőrizni a fiókodat',
    },
    passkey: {
      blockButton__passkey: 'Passkey használata',
      subtitle:
        'A passkey használata megerősíti a személyazonosságodat. Az eszközöd kérheti az ujjlenyomatodat, arcodat vagy a képernyőzáradat.',
      title: 'Passkey használata',
    },
    password: {
      actionLink: 'Másik módszer használata',
      subtitle: 'Add meg a jelenlegi jelszavadat a folytatáshoz',
      title: 'Ellenőrzés szükséges',
    },
    phoneCode: {
      formTitle: 'Visszaigazoló kód',
      resendButton: 'Nem kaptad meg a kódot? Újraküldés',
      subtitle: 'Írd be a telefonodra küldött kódot a folytatáshoz',
      title: 'Ellenőrzés szükséges',
    },
    phoneCodeMfa: {
      formTitle: 'Visszaigazoló kód',
      resendButton: 'Nem kaptad meg a kódot? Újraküldés',
      subtitle: 'Írd be a telefonodra küldött kódot a folytatáshoz',
      title: 'Ellenőrzés szükséges',
    },
    totpMfa: {
      formTitle: 'Visszaigazoló kód',
      subtitle: 'Írd be a hitelesítő alkalmazás által generált kódot a folytatáshoz',
      title: 'Ellenőrzés szükséges',
    },
  },
  signIn: {
    accountSwitcher: {
      action__addAccount: 'Fiók hozzáadása',
      action__signOutAll: 'Kijelentkezés minden fiókból',
      subtitle: 'Válaszd ki a fiókot amivel folytatni szeretnéd',
      title: 'Válassz egy fiókot',
    },
    alternativeMethods: {
      actionLink: 'Segítség kérése',
      actionText: 'Nincs ezekből egyik sem ?',
      blockButton__backupCode: 'Tartalék kód használata',
      blockButton__emailCode: 'Email kód a {{identifier}}-nak/nek',
      blockButton__emailLink: 'Email link a {{identifier}}-nak/nek',
      blockButton__passkey: 'Bejelentkezés passkey-vel',
      blockButton__password: 'Bejelentkezés jelszóval',
      blockButton__phoneCode: 'SMS kód {{identifier}}-nak/nek',
      blockButton__totp: 'Hitelesítő app használata',
      getHelp: {
        blockButton__emailSupport: 'Segítség kérése emailben',
        content:
          'Ha bármilyen problémád van a bejelentkezéssel a fiókodba, küldj nekünk egy emailt, és visszaállítjuk a fiókodat, amint lehetséges.',
        title: 'Segítség kérés',
      },
      subtitle: 'Problémád akadt? Ezek közül bármelyik bejelentkezési módot választhatod.',
      title: 'Bejelentkezés más módon',
    },
    alternativePhoneCodeProvider: {
      formTitle: 'Visszaigazoló kód',
      resendButton: 'Nem kaptad meg a kódot? Újraküldés',
      subtitle: 'hogy folytathasd a(z) {{applicationName}}',
      title: 'Ellenőrizd a(z) {{provider}} üzeneteidet',
    },
    backupCodeMfa: {
      subtitle: 'A tartalék kód az, amit akkor kaptál, amikor beállítottad a kétlépcsős azonosítást',
      title: 'Írd be a tartalék kódot',
    },
    emailCode: {
      formTitle: 'Visszaigazoló kód',
      resendButton: 'Nem kaptad meg a kódot? Újraküldés',
      subtitle: 'hogy folytathasd a(z) {{applicationName}}',
      title: 'Ellenőrizd az emailed',
    },
    emailCodeMfa: {
      formTitle: 'Ellenőrizd az emailed',
      resendButton: 'Nem kaptad meg a kódot? Újraküldés',
      subtitle: 'hogy folytathasd a(z) {{applicationName}}',
      title: 'Ellenőrizd az emailed',
    },
    emailLink: {
      clientMismatch: {
        subtitle:
          'A folytatáshoz nyisd meg a megerősítő linket azon az eszközön és böngészőben, ahonnan a bejelentkezést kezdeményezted',
        title: 'A megerősítő link érvénytelen ehhez az eszközhöz',
      },
      expired: {
        subtitle: 'Menj vissza az eredeti lapra a folytatáshoz.',
        title: 'Ez a megerősítő link lejárt',
      },
      failed: {
        subtitle: 'Menj vissza az eredeti lapra a folytatáshoz.',
        title: 'Ez a megerősítő link érvénytelen',
      },
      formSubtitle: 'Használd a megerősítő linket, amit a emailben kaptál',
      formTitle: 'Megerősítő link',
      loading: {
        subtitle: 'Hamarosan átirányítunk',
        title: 'Bejelentkezés folyamatban...',
      },
      resendButton: 'Nem kaptál linket? Újraküldés',
      subtitle: 'hogy folytathasd a(z) {{applicationName}}',
      title: 'Ellenőrizd az emailed',
      unusedTab: {
        title: 'Ezt a lapot bezárhatod',
      },
      verified: {
        subtitle: 'Hamarosan átirányítunk',
        title: 'Sikeres bejelentkezés',
      },
      verifiedSwitchTab: {
        subtitle: 'Menj vissza az eredeti lapra a folyatáshoz',
        subtitleNewTab: 'Menj át az újonan megnyitott lapra a folytatáshoz',
        titleNewTab: 'Egy másik lapon bejelezkeztél be',
      },
    },
    emailLinkMfa: {
      formSubtitle: 'Használja az e-mail címére küldött megerősítő linket',
      resendButton: 'Nem kapta meg a linket? Újraküldés',
      subtitle: 'a folytatáshoz a {{applicationName}} alkalmazásban',
      title: 'Ellenőrizze az e-mailjét',
    },
    enterpriseConnections: {
      subtitle: 'Válaszd ki a vállalati fiókot, amellyel folytatni szeretnéd.',
      title: 'Válaszd ki a vállalati fiókodat',
    },
    forgotPassword: {
      formTitle: 'Jelszó visszaállító kód',
      resendButton: 'Nem kaptad meg a kódot? Újraküldés',
      subtitle: 'hogy visszaállíthasd a jelszavad',
      subtitle_email: 'Először, írd be a kódot amit emailben kaptál',
      subtitle_phone: 'Először, írd be a kódot amit a telefonodra kaptál',
      title: 'Jelszó visszaállítás',
    },
    forgotPasswordAlternativeMethods: {
      blockButton__resetPassword: 'Állítsd vissza a jelszavad',
      label__alternativeMethods: 'Vagy jelentkezz be más módon',
      title: 'Efelejtetted a jelszavad?',
    },
    newDeviceVerificationNotice:
      'Új eszközről jelentkezel be. Kérünk egy ellenőrzést, hogy fiókod biztonságban maradjon.',
    noAvailableMethods: {
      message: 'Nem lehet bejelentkezni. Nincs elérhető hitelesítő tényező.',
      subtitle: 'Hiba történt',
      title: 'Nem lehetett bejelentkezni',
    },
    passkey: {
      subtitle:
        'A Passkey-d használata megerősíti, hogy te vagy az. Az eszközöd kérheti az ujjlenyomatod, arcod vagy a képernyőzárad.',
      title: 'Használd a passkeydet',
    },
    password: {
      actionLink: 'Másik mód használata',
      subtitle: 'Írd be a fiókhoz tartozó jelszavad',
      title: 'Írd be a jelszavad',
    },
    passwordCompromised: {
      title: 'Kompromittált jelszó',
    },
    passwordPwned: {
      title: 'Jelszó kompromitálódott',
    },
    passwordUntrusted: {
      title: 'Nem megbízható jelszó',
    },
    phoneCode: {
      formTitle: 'Visszaigazoló kód',
      resendButton: 'Nem kaptad meg a kódot? Újraküldés',
      subtitle: 'hogy folytathasd a(z) {{applicationName}}',
      title: 'Ellenőrizd a telefonod',
    },
    phoneCodeMfa: {
      formTitle: 'Visszaigazoló kód',
      resendButton: 'Nem kaptad meg a kódot? Újraküldés',
      subtitle: 'A folytatáshoz, kérlek írd be a visszaigazoló kódot, amit a telefonodra küldtünk.',
      title: 'Ellenőrizd a telefonod',
    },
    resetPassword: {
      formButtonPrimary: 'Jelszó visszaállítása',
      requiredMessage: 'Biztonsági okokból, muszáj megváltoztatnod a jelszavadat.',
      successMessage: 'A jelszavad sikeresen megváltozott. A bejelentkezés folyamatban, kérlek várj.',
      title: 'Új jelszó beállítása',
    },
    resetPasswordMfa: {
      detailsLabel: 'Vissza kell igazolnod az identitásod, mielőtt visszaállítod a jelszavad',
    },
    start: {
      actionLink: 'Regisztráció',
      actionLink__join_waitlist: 'Csatlakozás a várólistához',
      actionLink__use_email: 'Email használata',
      actionLink__use_email_username: 'Használd az emailded vagy a felhasználóneved',
      actionLink__use_passkey: 'Passkey használata',
      actionLink__use_phone: 'Telefon használata',
      actionLink__use_username: 'Felhasználónév használata',
      actionText: 'Nincs fiókod?',
      actionText__join_waitlist: 'Korai hozzáférést szeretnél?',
      alternativePhoneCodeProvider: {
        actionLink: 'Másik módszer használata',
        label: '{{provider}} telefonszám',
        subtitle: 'Add meg a telefonszámod, hogy visszaigazoló kódot kapj a(z) {{provider}} alkalmazásban.',
        title: 'Bejelentkezés a(z) {{applicationName}} fiókba {{provider}} segítségével',
      },
      subtitle: 'Üdv újra! A folytatáshoz kérlek jelentkezz be.',
      subtitleCombined: undefined,
      title: 'Bejelentkezés a(z) {{applicationName}} fiókba',
      titleCombined: 'Folytatás a(z) {{applicationName}} fiókba',
    },
    totpMfa: {
      formTitle: 'Visszaigazoló kód',
      subtitle: 'A folytatáshoz, kérlek írd be a visszaigazoló kódot, amit a hitelesítő app készített.',
      title: 'Két lépécsős azonosítás',
    },
    web3Solana: {
      subtitle: 'Válasszon alább egy tárcát a bejelentkezéshez',
      title: 'Bejelentkezés Solanával',
    },
  },
  signInEnterPasswordTitle: 'Írd be a jelszavad',
  signUp: {
    alternativePhoneCodeProvider: {
      resendButton: 'Nem kaptad meg a kódot? Újraküldés',
      subtitle: 'Írd be a visszaigazoló kódot, amit a(z) {{provider}} alkalmazáson kaptál',
      title: '{{provider}} ellenőrzése',
    },
    continue: {
      actionLink: 'Bejelentkezés',
      actionText: 'Van már fiókod?',
      subtitle: 'Kérlek töltsd ki a hátralévő mezőket a folytatáshoz',
      title: 'Töltsd ki a hiányzó mezőket',
    },
    emailCode: {
      formSubtitle: 'Írd be a visszaigazoló kódot, amit emailben kaptál',
      formTitle: 'Viszaigazoló kód',
      resendButton: 'Nem kaptad meg a kódot? Újraküldés',
      subtitle: 'Írd be a visszaigazoló kódot, amit emailben kaptál',
      title: 'Email megerősítése',
    },
    emailLink: {
      clientMismatch: {
        subtitle:
          'A folytatáshoz nyisd meg a megerősítő linket azon az eszközön és böngészőben, ahonnan a regisztrációt kezdeményezted',
        title: 'A megerősítő link érvénytelen ehhez az eszközhöz',
      },
      formSubtitle: 'Használd a  visszaigazoló linket, amit a emailben kaptál',
      formTitle: 'Visszaigazoló link',
      loading: {
        title: 'Regisztrálás...',
      },
      resendButton: 'Nem kaptad meg a kódot? Újraküldés',
      subtitle: 'hogy folytasd a(z) {{applicationName}}',
      title: 'Erősítsd meg az email címed',
      verified: {
        title: 'Sikeres regisztráció',
      },
      verifiedSwitchTab: {
        subtitle: 'Menj az újonan nyitott lapra, a folytatáshoz',
        subtitleNewTab: 'Menj vissza az előző lapra a folytatáshoz',
        title: 'Sikeresen megerősítetted az email címed',
      },
    },
    enterpriseConnections: {
      subtitle: 'Válaszd ki a vállalati fiókot, amellyel folytatni szeretnéd.',
      title: 'Válaszd ki a vállalati fiókodat',
    },
    legalConsent: {
      checkbox: {
        label__onlyPrivacyPolicy: 'Elfogadom az {{ privacyPolicyLink || link("Adatkezelési Tájékoztatót") }}',
        label__onlyTermsOfService: 'Elfogadom az {{ termsOfServiceLink || link("Általános Szerződési Feltételeket") }}',
        label__termsOfServiceAndPrivacyPolicy:
          'Elfogadom az {{ termsOfServiceLink || link("Általános Szerződési Feltételeket") }} és az {{ privacyPolicyLink || link("Adatkezelési Tájékoztatót") }}',
      },
      continue: {
        subtitle: 'A folytatáshoz kérlek, olvasd el és fogadd el a feltételeket',
        title: 'Jogi nyilatkozat',
      },
    },
    phoneCode: {
      formSubtitle: 'Írd be a visszaigazoló kódot, amit a telefondra kaptál',
      formTitle: 'Visszaigazoló kód',
      resendButton: 'Nem kaptad meg a kódot? Újraküldés',
      subtitle: 'Írd be a visszaigazoló kódot, amit a telefonodra kaptál',
      title: 'Erősítsd meg a telefonszámod',
    },
    restrictedAccess: {
      actionLink: 'Bejelentkezés',
      actionText: 'Van már fiókod?',
      blockButton__emailSupport: 'Segítség kérése emailben',
      blockButton__joinWaitlist: 'Csatlakozás a várólistához',
      subtitle:
        'A regisztráció jelenleg nem elérhető. Ha úgy gondolod, hogy hozzáféréssel kellene rendelkezned, kérlek vedd fel a kapcsolatot az ügyfélszolgálattal.',
      subtitleWaitlist:
        'A regisztráció jelenleg nem elérhető. Ha elsőként szeretnél értesülni az indulásról, csatlakozz a várólistához.',
      title: 'Hozzáférés korlátozva',
    },
    start: {
      actionLink: 'Bejelentkezés',
      actionLink__use_email: 'Email használata',
      actionLink__use_phone: 'Telefon használata',
      actionText: 'Van már fiókod?',
      alternativePhoneCodeProvider: {
        actionLink: 'Másik módszer használata',
        label: '{{provider}} telefonszám',
        subtitle: 'Add meg a telefonszámod, hogy visszaigazoló kódot kapj a(z) {{provider}} alkalmazásban.',
        title: 'Regisztráció a(z) {{applicationName}} fiókba {{provider}} segítségével',
      },
      subtitle: 'Üdv! Kérlek add meg az adatokat, hogy elkezdhesd.',
      subtitleCombined: 'Üdv! Kérlek add meg az adatokat, hogy elkezdhesd.',
      title: 'Fiók létrehozása',
      titleCombined: 'Fiók létrehozása',
    },
    web3Solana: {
      subtitle: 'Válasszon alább egy tárcát a regisztrációhoz',
      title: 'Regisztráció Solanával',
    },
  },
  socialButtonsBlockButton: 'Folytatás {{provider|titleize}} segítségével',
  socialButtonsBlockButtonManyInView: '{{provider|titleize}}',
  taskChooseOrganization: {
    alerts: {
      organizationAlreadyExists:
        'Már létezik szervezet az észlelt cégnévhez ({{organizationName}}) és {{organizationDomain}}. Csatlakozz meghívással.',
    },
    chooseOrganization: {
      action__createOrganization: 'Szervezet létrehozása',
      action__invitationAccept: 'Csatlakozás',
      action__suggestionsAccept: 'Csatlakozás kérése',
      subtitle: 'Csatlakozz egy meglévő szervezethez vagy hozz létre újat',
      subtitle__createOrganizationDisabled: 'Csatlakozz egy meglévő szervezethez',
      suggestionsAcceptedLabel: 'Elfogadásra vár',
      title: 'Válassz szervezetet',
    },
    createOrganization: {
      formButtonReset: 'Mégse',
      formButtonSubmit: 'Folytatás',
      formFieldInputPlaceholder__name: 'Az én szervezetem',
      formFieldInputPlaceholder__slug: 'az-en-szervezetem',
      formFieldLabel__name: 'Név',
      formFieldLabel__slug: 'Slug',
      subtitle: 'Adja meg szervezete adatait a folytatáshoz',
      title: 'Állítsa be szervezetét',
    },
    organizationCreationDisabled: {
      subtitle: 'Kérjen meghívót a szervezet adminisztrátorától.',
      title: 'Egy szervezethez kell tartoznia',
    },
    signOut: {
      actionLink: 'Kijelentkezés',
      actionText: 'Bejelentkezve: {{identifier}}',
    },
  },
  taskResetPassword: {
    formButtonPrimary: 'Jelszó visszaállítása',
    signOut: {
      actionLink: 'Kijelentkezés',
      actionText: 'Bejelentkezve: {{identifier}}',
    },
    subtitle: 'A fiókodhoz új jelszó szükséges, mielőtt folytathatnád',
    title: 'Jelszó visszaállítása',
  },
  taskSetupMfa: {
    badge: 'Kétlépcsős azonosítás beállítása',
    signOut: {
      actionLink: 'Kijelentkezés',
      actionText: 'Bejelentkezve: {{identifier}}',
    },
    smsCode: {
      addPhone: {
        formButtonPrimary: 'Folytatás',
        infoText:
          'Egy visszaigazoló kódot tartalmazó szöveges üzenetet küldünk erre a telefonszámra. Üzenet- és adatdíjak felmerülhetnek.',
      },
      addPhoneNumber: 'Telefonszám hozzáadása',
      cancel: 'Mégse',
      subtitle: 'Válassz telefonszámot az SMS kód kétlépcsős azonosításhoz',
      success: {
        finishButton: 'Folytatás',
        message1:
          'A kétlépcsős azonosítás be van kapcsolva. Bejelentkezéskor egy visszaigazoló kódot kell megadnod, amelyet erre a telefonszámra küldünk.',
        message2:
          'Mentsd el ezeket a tartalék kódokat, és tárold biztonságos helyen. Ha elveszíted a hitelesítő eszközöd, a tartalék kódokkal tudsz belépni.',
        title: 'SMS kód ellenőrzés bekapcsolva',
      },
      title: 'SMS kód ellenőrzés hozzáadása',
      verifyPhone: {
        formButtonPrimary: 'Folytatás',
        formTitle: 'Visszaigazoló kód',
        resendButton: 'Nem kaptad meg a kódot? Újraküldés',
        subtitle: 'Írd be a visszaigazoló kódot, amelyet ide küldtünk:',
        title: 'Telefonszám ellenőrzése',
      },
    },
    start: {
      methodSelection: {
        phoneCode: 'SMS kód',
        totp: 'Hitelesítő alkalmazás',
      },
      subtitle: 'Válaszd ki, melyik módszerrel szeretnéd megvédeni a fiókodat egy extra biztonsági réteggel',
      title: 'Kétlépcsős azonosítás beállítása',
    },
    totpCode: {
      addAuthenticatorApp: {
        buttonAbleToScan__nonPrimary: 'Inkább olvasd be a QR kódot',
        buttonUnableToScan__nonPrimary: 'Nem tudod beolvasni a QR kódot?',
        formButtonPrimary: 'Folytatás',
        formButtonReset: 'Mégse',
        infoText__ableToScan:
          'Állíts be egy új belépési módot a hitelesítő alkalmazásodban, és olvasd be a QR kódot, hogy összekösd a fiókoddal.',
        infoText__unableToScan:
          'Állíts be egy új bejelentkezési módot a hitelesítő alkalmazásodban, és írd be az alábbi kulcsot.',
        inputLabel__unableToScan1:
          'Bizonyosodj meg, hogy a Time-based vagy a One-time passwords be van kapcsolva, majd fejezd be a fiók összekötését.',
      },
      success: {
        finishButton: 'Folytatás',
        message1:
          'A kétlépcsős azonosítás be van kapcsolva. Bejelentkezéskor meg kell adnod a hitelesítő alkalmazás által generált kódot.',
        message2:
          'Mentsd el ezeket a tartalék kódokat, és tárold biztonságos helyen. Ha elveszíted a hitelesítő eszközöd, a tartalék kódokkal tudsz belépni.',
        title: 'Hitelesítő alkalmazás ellenőrzés bekapcsolva',
      },
      title: 'Hitelesítő alkalmazás hozzáadása',
      verifyTotp: {
        formButtonPrimary: 'Folytatás',
        formButtonReset: 'Mégse',
        formTitle: 'Visszaigazoló kód',
        subtitle: 'Írd be a hitelesítő alkalmazás által generált visszaigazoló kódot',
        title: 'Hitelesítő alkalmazás hozzáadása',
      },
    },
  },
  unstable__errors: {
    already_a_member_in_organization: '{{email}} már tagja a szervezetnek.',
    api_key_name_already_exists: 'Ez az API kulcs név már létezik.',
    api_key_usage_exceeded: 'Elérted a használati korlátot. A korlát eltávolításához válts fizetős csomagra.',
    avatar_file_size_exceeded: 'A fájl mérete meghaladja a 10 MB-os maximális korlátot. Kérlek válassz kisebb fájlt.',
    avatar_file_type_invalid: 'A fájltípus nem támogatott. Kérlek tölts fel JPG, PNG, GIF vagy WEBP képet.',
    captcha_invalid:
      'Biztonsági okokból a regisztráció sikertelen volt. Kérlek frissítsd az oldalt, hogy újra próbálhasd, vagy kérj támogatást.',
    captcha_unavailable:
      'Bot érvényesítése miatt, a regisztráció sikertelen volt. Kérlek frissítsd az oldalt, hogy újra próbálhasd, vagy kérj támogatást.',
    form_code_incorrect: undefined,
    form_email_address_blocked:
      'Az ideiglenes e-mail szolgáltatások nem támogatottak. Kérlek, használd a szokásos e-mail címedet a fiók létrehozásához.',
    form_identifier_exists__email_address: 'Ez az email cím már foglalt. Kérlek próbálj egy másikat.',
    form_identifier_exists__phone_number: 'Ez a telefonszám már foglalt. Kérlek próbálj egy másikat.',
    form_identifier_exists__username: 'Ez a felhasználónév már foglalt. Kérlek próbálj egy másikat.',
    form_identifier_not_found: 'Nem találtunk fiókot ezekkel a részletekkel.',
    form_new_password_matches_current: 'Az új jelszó nem egyezhet meg a jelenlegi jelszóval.',
    form_param_format_invalid: undefined,
    form_param_format_invalid__email_address: 'Az email címnek érvényes email címnek kell lennie.',
    form_param_format_invalid__phone_number: 'A telefonszámnak érvényes telefonszámnak kell lennie.',
    form_param_max_length_exceeded__first_name: 'A keresztnév nem lehet hosszabb, mint 256 karakter.',
    form_param_max_length_exceeded__last_name: 'A vezetéknév nem lehet hosszabb, mint 256 karakter.',
    form_param_max_length_exceeded__name: 'A név nem lehet hosszabb mint 256 karakter.',
    form_param_nil: undefined,
    form_param_type_invalid: undefined,
    form_param_type_invalid__email_address: undefined,
    form_param_type_invalid__phone_number: undefined,
    form_param_value_invalid: undefined,
    form_password_compromised__sign_in: undefined,
    form_password_incorrect: undefined,
    form_password_length_too_short: 'A jelszavad túl rövid. Legalább 8 karakter hosszúnak kell lennie.',
    form_password_not_strong_enough: 'A jelszó nem elég erős',
    form_password_or_identifier_incorrect:
      'A jelszó vagy az e-mail cím helytelen. Próbáld újra vagy használj másik módszert.',
    form_password_pwned:
      'Úgy látjuk, hogy ez a jelszó kiszivárgott, ezért ezt nem használhatod, kérlek próbálj egy másik jelszót.',
    form_password_pwned__sign_in:
      'Úgy látjuk, hogy ez a jelszó kiszivárgott, ezért ezt nem használhatod, kérlek állítsd át a jelszavad.',
    form_password_size_in_bytes_exceeded:
      'A jelszavad több bájtot tartalmaz mint a megadott maximum, kérlek rövidítsd vagy törölj ki néhány speciális karaktert.',
    form_password_untrusted__sign_in:
      'A jelszavad kompromittálódhatott. A fiókod védelme érdekében kérlek folytasd egy másik bejelentkezési módszerrel. A bejelentkezés után kötelező lesz a jelszavad visszaállítása.',
    form_password_validation_failed: 'Helytelen jelszó',
    form_username_invalid_character: undefined,
    form_username_invalid_length: 'A felhasználónévnek {{min_length}} és {{max_length}} karakter között kell lennie.',
    form_username_needs_non_number_char: 'A felhasználónévnek legalább egy nem numerikus karaktert kell tartalmaznia.',
    identification_deletion_failed: 'Nem törölheted ki az utolsó azonosítód.',
    insufficient_seats_change_plan: undefined,
    insufficient_seats_contact_support: undefined,
    not_allowed_access:
      "Az email címed vagy a telefonszámod nem használható regisztrációhoz. Ez lehet, mert az email címedben vagy a telefonszámodban szerepel a '+', '=', '#' vagy '.' karakter, vagy az email címedben vagy a telefonszámodban szerepel egy időzített email szolgáltató vagy kizárt tartomány. Ha úgy gondolja, hogy ez hiba, vegye fel velünk a kapcsolatot.",
    oauth_access_denied: undefined,
    organization_domain_blocked: undefined,
    organization_domain_common: undefined,
    organization_domain_exists_for_enterprise_connection: undefined,
    organization_membership_quota_exceeded:
      'Elérted a szervezeti tagságok korlátját, beleértve a függőben lévő meghívásokat.',
    organization_minimum_permissions_needed: undefined,
    organization_not_found_or_unauthorized:
      'Már nem vagy tagja ennek a szervezetnek. Kérlek válassz vagy hozz létre egy másikat.',
    organization_not_found_or_unauthorized_with_create_organization_disabled:
      'Már nem vagy tagja ennek a szervezetnek. Kérlek válassz egy másikat.',
    passkey_already_exists: 'Egy passkey már regisztrálva van ehhez az eszközhöz.',
    passkey_not_supported: 'Passkeyk nem támogatottak ezen az eszközön.',
    passkey_pa_not_supported: 'A regisztrációhoz egy platform hitelesítő kell, de ez az eszköz ezt nem támogatja.',
    passkey_registration_cancelled: 'Passkey regisztráció megszakadt vagy lejárt.',
    passkey_retrieval_cancelled: 'Passkey visszaigazolás megszakadt vagy lejárt.',
    passwordComplexity: {
      maximumLength: 'kevesebb mint {{length}} karaktert',
      minimumLength: '{{length}} vagy több karaktert',
      requireLowercase: 'egy kisbetűt',
      requireNumbers: 'egy számot',
      requireSpecialCharacter: 'egy speciális karaktert',
      requireUppercase: 'egy nagybetű',
      sentencePrefix: 'A jelszavadnak tartalmaznia kell',
    },
    phone_number_exists: 'Ez a telefonszám már foglalt. Kérlek próbálj meg egy másikat.',
    session_exists: 'Már be vagy jelentkezve.',
    web3_missing_identifier: 'Nem található Web3 tárca bővítmény. Kérlek telepíts egyet a folytatáshoz.',
    web3_signature_request_rejected: 'Elutasította az aláírási kérelmet. Kérjük, próbálja meg újra a folytatáshoz.',
    web3_solana_signature_generation_failed:
      'Hiba történt az aláírás létrehozása közben. Kérjük, próbálja meg újra a folytatáshoz.',
    zxcvbn: {
      couldBeStronger: 'A jelszavad, jó, de lehetne erősebb. Adj hozzá több karaktert.',
      goodPassword: 'A jelszavad megfelel az elvárásoknak.',
      notEnough: 'Nem elég erős a jelszavad.',
      suggestions: {
        allUppercase: 'Változtass meg néhány betűt nagybetűre, de ne mindet.',
        anotherWord: 'Adj hozzá kevésbé gyakori szavakat.',
        associatedYears: 'Kerüld el a hozzád kapcsolható évek használatát',
        capitalization: 'Ne csak az első betű legyen nagy betű.',
        dates: 'Kerüld el a hozzád köthető dátumok és évek használatát.',
        l33t: "Kerüld el a kiszámítható betű behelyettesítéseket, mint a '@' az 'a' helyett.",
        longerKeyboardPattern: 'Használj hosszabb billentyűzet mintákat, és változtasd meg a gépelés irányát többször.',
        noNeed:
          'Úgy is létrehozhatsz erős jelszót, hogy nem használsz speciális karaktereket, számokat, vagy nagybetűket.',
        pwned: 'Ha máshol is használod ezt a jelszót, akkor változtasd meg.',
        recentYears: 'Kerüld el a közelmúlt évek használatát.',
        repeated: 'Kerüld el a szó- vagy karakterismétlést',
        reverseWords: 'Kerüld el a szavak visszafelé írását.',
        sequences: 'Kerüld el a gyakori karakter sorozatokat.',
        useWords: 'Használj több szót, de kerüld el a gyakori kifejezéseket.',
      },
      warnings: {
        common: 'Ez egy gyakran használt jelszó',
        commonNames: 'A gyakori nevek könnyen kitalálhatóak.',
        dates: 'A dátumokat könnyű kitalálni.',
        extendedRepeat: 'Ismétlődő karakter sorozatok, mint "abcabcabc" könnyen kitalálhatóak.',
        keyPattern: 'A rövid billentyűzetminták könnyen kitalálhatóak.',
        namesByThemselves: 'A nevek könnyen kitalálhatóak.',
        pwned: 'A jelszavad kiszivárgott egy adatszivárgás során az Interneten. Válassz egy másikat.',
        recentYears: 'Az elmúlt évek könnyen kitalálhatóak.',
        sequences: 'Gyakori karakter sorozatok, mint "abc" könnyen kitalálhatóak.',
        similarToCommon: 'Ez hasonlít egy gyakran használt jelszóhoz.',
        simpleRepeat: 'Ismétlődő karakterek, mint az "aaa" könnyen kitalálhatóak.',
        straightRow: 'Egyenes sor a billentyűzeten, mint az "asdf" könnyen kitalálhatóak.',
        topHundred: 'Ez egy gyakran használt jelszó',
        topTen: 'Ez egy nagyon gyakori jelszó',
        userInputs: 'Ne tartalmazzon, személyes, vagy az oldalhoz köthető információt.',
        wordByItself: 'Egyszavas jelszavak könnyen kitalálhatóak.',
      },
    },
  },
  userButton: {
    action__addAccount: 'Fiók hozzáadása',
    action__closeUserMenu: 'Felhasználói menü bezárása',
    action__manageAccount: 'Fiók kezelése',
    action__openUserMenu: 'Felhasználói menü megnyitása',
    action__signOut: 'Kijelentkezés',
    action__signOutAll: 'Kijelentkezés minden fiókból',
    label__userButtonPopover: 'Fiókpanel',
    label__accountActions: 'Fiókműveletek',
    label__activeSessions: 'Aktív munkamenetek',
  },
  userProfile: {
    apiKeysPage: {
      title: 'API kulcsok',
    },
    backupCodePage: {
      actionLabel__copied: 'Kimásolva!',
      actionLabel__copy: 'Az összes kimásolása',
      actionLabel__download: '.txt letöltése',
      actionLabel__print: 'Nyomtatás',
      infoText1: 'A tartalék kódok be lesznek kapcsolva ehhez a fiókhoz.',
      infoText2:
        'A tartalék kódokat tartsd titokban, és tárold biztonságos helyen. Újragenerálhatod a tartalék kódokat, ha azt gondolod, hogy kiszivárogtak.',
      subtitle__codelist: 'Tárold őket biztonságos helyen, és tartsd titokban.',
      successMessage:
        'A tartalék kódok bekapcsolva. Használhatod ezeket, hogy belépj a fiókodba, ha nem férsz hozzá a hitelesítő eszközhöz. Mindegyik kódot egyszer tudod használni.',
      successSubtitle: 'Használhatod ezeket is, hogy belépj a fiókodba, ha nem férsz hozzá a hitelesítő eszközödhöz.',
      title: 'Tartalék kód megerősítés hozzáadása',
      title__codelist: 'Tartalék kódok',
    },
    billingPage: {
      paymentHistorySection: {
        empty: 'Nincs fizetési előzmény',
        notFound: 'A fizetési kísérlet nem található',
        tableHeader__amount: 'Összeg',
        tableHeader__date: 'Dátum',
        tableHeader__status: 'Állapot',
      },
      paymentMethodsSection: {
        actionLabel__default: 'Beállítás alapértelmezettként',
        actionLabel__remove: 'Eltávolítás',
        add: 'Új fizetési mód hozzáadása',
        addSubtitle: 'Adj hozzá egy új fizetési módot a fiókodhoz.',
        cancelButton: 'Mégse',
        formButtonPrimary__add: 'Fizetési mód hozzáadása',
        formButtonPrimary__pay: '{{amount}} fizetése',
        payWithTestCardButton: 'Fizetés teszt kártyával',
        removeMethod: {
          messageLine1: '{{identifier}} eltávolításra kerül erről a fiókról.',
          messageLine2:
            'Nem fogod tudni használni ezt a fizetési forrást, és a hozzá kapcsolódó ismétlődő előfizetések nem fognak működni.',
          successMessage: '{{paymentMethod}} eltávolítva a fiókodról.',
          title: 'Fizetési mód eltávolítása',
        },
        title: 'Fizetési módok',
      },
      start: {
        headerTitle__payments: 'Fizetések',
        headerTitle__plans: 'Csomagok',
        headerTitle__statements: 'Kivonatok',
        headerTitle__subscriptions: 'Előfizetés',
      },
      statementsSection: {
        empty: 'Nincsenek megjeleníthető kivonatok',
        itemCaption__paidForPlan: 'Fizetve a(z) {{plan}} {{period}} csomagért',
        itemCaption__payerCredit: 'Jóváírás a fiók egyenlegből',
        itemCaption__proratedCredit: 'Arányosított jóváírás az előző előfizetés részleges használatáért',
        itemCaption__subscribedAndPaidForPlan: 'Előfizetve és fizetve a(z) {{plan}} {{period}} csomagért',
        notFound: 'Kivonat nem található',
        tableHeader__amount: 'Összeg',
        tableHeader__date: 'Dátum',
        title: 'Kivonatok',
        totalPaid: 'Összesen fizetve',
      },
      subscriptionsListSection: {
        actionLabel__manageSubscription: 'Kezelés',
        actionLabel__newSubscription: 'Előfizetés csomagra',
        actionLabel__switchPlan: 'Csomag váltása',
        overview: undefined,
        tableHeader__edit: 'Szerkesztés',
        tableHeader__plan: 'Csomag',
        tableHeader__startDate: 'Kezdő dátum',
        title: 'Előfizetés',
      },
      subscriptionsSection: {
        actionLabel__default: 'Kezelés',
      },
      switchPlansSection: {
        title: 'Csomag váltása',
      },
      title: 'Számlázás',
    },
    connectedAccountPage: {
      formHint: 'Válassz egy szolgáltatót, amit összekötsz a fiókoddal.',
      formHint__noAccounts: 'Nincs elérhető külső fiók szolgáltató.',
      removeResource: {
        messageLine1: '{{identifier}} el lesz távolítva ebből a fiókból.',
        messageLine2:
          'Nem fogod tudni használni ezt a kapcsolt fiókot. Bármilyen ettől függő szolgáltatás nem fog működni.',
        successMessage: '{{connectedAccount}} eltávolítva a fiókdból.',
        title: 'Kapcsolt fiók eltávolítása',
      },
      socialButtonsBlockButton: '{{provider|titleize}}',
      successMessage: 'A szolgátató hozzá lett adva a fiókodhoz.',
      title: 'Kapcsolt fiók hozzáadása',
    },
    deletePage: {
      actionDescription: 'Írd be, hogy "Delete account" a folytatáshoz.',
      confirm: 'Fiók törlése',
      messageLine1:
        'Biztosan törölni szeretné fiókját? Egyes kapcsolódó adatok megőrzésre kerülhetnek. A teljes adattörlés kéréséhez forduljon az ügyfélszolgálathoz.',
      messageLine2: 'Ez a művelet végleges és visszafordíthatatlan.',
      title: 'Fiók törlése',
    },
    emailAddressPage: {
      emailCode: {
        formHint: 'Egy visszaigazoló kódot tartalmazó emailt fogunk küldeni erre az email címre.',
        formSubtitle: 'Írd be a visszaigazoló kódot, amit a(z) {{identifier}} címre küldtünk.',
        formTitle: 'Visszaigazoló kód',
        resendButton: 'Nem kaptad meg a kódot? Újraküldés',
        successMessage: 'Az email: {{identifier}} hozzá lett adva a fiókodhoz.',
      },
      emailLink: {
        formHint: 'Egy visszaigazoló linket tartalmazó emailt fogunk küldeni erre az email címre.',
        formSubtitle: 'Kattints a visszaigazoló linkre az emailben, amit ide küldtünk: {{identifier}}',
        formTitle: 'Visszaigazoló link',
        resendButton: 'Nem kaptad meg a linket? Újraküldés',
        successMessage: 'Az email: {{identifier}} hozzá lett adva a fiókodhoz.',
      },
      enterpriseSSOLink: {
        formButton: 'Kattints a bejelentkezéshez',
        formSubtitle: 'Fejezd be a bejelentkezést a(z) {{identifier}} fiókkal',
      },
      formHint: 'Ezt az email címet ellenőrizned kell, mielőtt hozzáadhatnád a fiókodhoz.',
      removeResource: {
        messageLine1: '{{identifier}} el lesz távolítva ebből a fiókból.',
        messageLine2: 'Nem fogsz tudni többet bejelentkezni ezzel az email címmel.',
        successMessage: '{{emailAddress}} el lett távolítva a fiókodból.',
        title: 'Email cím törlése',
      },
      title: 'Email cím hozzáadása',
      verifyTitle: 'Email cím visszaigazolása',
    },
    formButtonPrimary__add: 'Hozzáadás',
    formButtonPrimary__continue: 'Folytatás',
    formButtonPrimary__finish: 'Befejezés',
    formButtonPrimary__remove: 'Eltávolítás',
    formButtonPrimary__save: 'Mentés',
    formButtonReset: 'Mégsem',
    mfaPage: {
      formHint: 'Válassz egy hitelesítő módszert, amit hozzá szeretnél adni.',
      title: 'Kétlépcsős azonosítás bekapcsolása',
    },
    mfaPhoneCodePage: {
      backButton: 'Létező szám használata',
      primaryButton__addPhoneNumber: 'Telefonszám hozzáadása',
      removeResource: {
        messageLine1: '{{identifier}} nem fog többet visszaigazoló kódot kapni, amikor belépsz.',
        messageLine2: 'A fiókód nem biztos, hogy olyan biztonságos lesz. Biztosan folytatod?',
        successMessage: 'Kétlépcsős SMS kód eltávolítva a {{mfaPhoneCode}} számhoz',
        title: 'Kétlépcsős azonosítás eltávolítása',
      },
      subtitle__availablePhoneNumbers:
        'Válassz egy telefonszámot, hogy regisztráld az SMS kód kétlépcsős azonosítást, vagy adj hozzá egy újat.',
      subtitle__unavailablePhoneNumbers:
        'Nincs elérhető telefonszám, az SMS kód kétlépcsős azonosításhoz. Kérlek adj hozzá egyet.',
      successMessage1:
        'Amikor belépsz, extra lépésként meg kell adnod a visszaigazoló kódot, amit elküldünk erre a telefonszámra.',
      successMessage2:
        'Tárold ezeket a tartalék kódokat, egy biztonságos helyen. Ha nem férsz hozzá a hitelesítő eszközödhöz, ezekkel tudsz belépni.',
      successTitle: 'SMS visszaigazoló kód hozzáadva',
      title: 'SMS visszaigazoló kód hozzáadása',
    },
    mfaTOTPPage: {
      authenticatorApp: {
        buttonAbleToScan__nonPrimary: 'Inkább olvasd be a QR kódot',
        buttonUnableToScan__nonPrimary: 'Nem tudod beolvasni a QR kódot?',
        infoText__ableToScan:
          'Állíts be egy új belépési módot, a hitelesítő alkalmazásodban és olvasd a QR kódot, hogy összekösd a fiókoddal.',
        infoText__unableToScan:
          'Állíts be egy új bejelentkezés módot a hitelesítő alkalmazásodban, írd be a kulcsot, amit lejjebb találsz.',
        inputLabel__unableToScan1:
          'Bizonyosodj meg, hogy a Time-based vagy a One-time passwords be van kapcsolva, majd fejezd be a fiók összekötését a következő kulccsal:',
        inputLabel__unableToScan2:
          'Alternatívaként, ha az hitelesítő alkalmazásod támogatja a TOTP URI-kat, a teljes URI-t is be lehet másolni.',
      },
      removeResource: {
        messageLine1:
          'Visszaigazoló kódok ebből a hitelesítő alkalmazásból, már nem fognak kelleni a bejelenetkezéshez.',
        messageLine2: 'Előfordulhat, hogy a fiókod nem lesz olyan biztonságos. Biztonsan folytatni szeretnéd?',
        successMessage: 'Kétlépcsős azonosítás hitelesítő alkalmazással eltávolítva.',
        title: 'Kétlépcsős azonosítás eltávolítása',
      },
      successMessage:
        'Kétlépcsős azonosítást bekapcsolva. Extra lépésként, amikor belépsz, meg kell adnod a visszaigazoló kódot a hitelesítő alkalmazásodból.',
      title: 'Hitelesítő alkalmazás hozzáadása',
      verifySubtitle: 'Írd be a visszaigazoló kódot, amit a hitelesítő alkalmazásodból.',
      verifyTitle: 'Visszaigazoló kód',
    },
    mobileButton__menu: 'Menü',
    navbar: {
      account: 'Profil',
      apiKeys: 'API kulcsok',
      billing: 'Számlázás',
      description: 'Fiók információk kezelése',
      security: 'Biztonság',
      title: 'Fiók',
    },
    passkeyScreen: {
      removeResource: {
        messageLine1: '{{name}} el lesz távolítva ebből a fiókból.',
        title: 'Passkey törlése',
      },
      subtitle__rename: 'Megváltoztathatod a passkey nevét, hogy könnyebb legyen megtalálni.',
      title__rename: 'Passkey átnevezése',
    },
    passwordPage: {
      checkboxInfoText__signOutOfOtherSessions:
        'Ajánlott kijelentkeztetni az összes olyan eszközt, ami a régi jelszavadat használja.',
      readonly: 'A jelszavad jelenleg nem módosítható, mert csak vállalati kapcsolattal tudsz belépni.',
      successMessage__set: 'A jelszavad beállítottuk.',
      successMessage__signOutOfOtherSessions: 'Minden más eszköz kijelentkeztetve.',
      successMessage__update: 'A jelszavad frissítésre került.',
      title__set: 'Jelszó beállítása',
      title__update: 'Jelszó frissítése',
    },
    phoneNumberPage: {
      infoText:
        'Egy szöveges üzenetet küldünk a visszaigazoló kóddal erre a számra. Az üzenet és az adatforgalom díjai érvényesek lehetnek.',
      removeResource: {
        messageLine1: '{{identifier}} el lesz távolítva ebből a fiókból.',
        messageLine2: 'Nem fogsz tudni többet bejelentkezni ezzel a telefonszámmal.',
        successMessage: '{{phoneNumber}} el lett távolítva a fiókodból.',
        title: 'Telefonszám eltávolítása',
      },
      successMessage: '{{identifier}} hozzá lett adva a fiókodhoz.',
      title: 'Telefonszám hozzáadása',
      verifySubtitle: 'Írd be a visszaigazóló kódot, amit a(z) {{identifier}} számra küldtünk.',
      verifyTitle: 'Telefonszám visszaigazolása',
    },
    plansPage: {
      title: 'Csomagok',
    },
    profilePage: {
      fileDropAreaHint: 'Ajánlott méret 1:1, 10MB-ig.',
      imageFormDestructiveActionSubtitle: 'Eltávolítás',
      imageFormSubtitle: 'Feltöltés',
      imageFormTitle: 'Profil kép',
      readonly: 'A profilod adatai, vállalati kapcsolatból származnak, így nem módosíthatóak.',
      successMessage: 'A profilod frissítve.',
      title: 'Profil frissítése',
    },
    start: {
      activeDevicesSection: {
        destructiveAction: 'Kijelentkezés az eszközről',
        title: 'Aktív eszközök',
      },
      connectedAccountsSection: {
        actionLabel__connectionFailed: 'Próbáld újra',
        actionLabel__reauthorize: 'Engedélyezd most',
        destructiveActionTitle: 'Eltávolítás',
        primaryButton: 'Fiók összekötése',
        subtitle__disconnected: 'Ez a fiók le lett választva.',
        subtitle__reauthorize:
          'A szükséges hatáskörök megváltozták, előfordulhat, hogy limitált funkcionalitást tapasztalhatsz. Kérlek, újra engedélyezd az alkalmazást, hogy elkerüld a hibákat.',
        title: 'Kapcsolt fiókok',
      },
      dangerSection: {
        deleteAccountButton: 'Fiók törlése',
        title: 'Fiók törlése',
      },
      emailAddressesSection: {
        destructiveAction: 'Email eltávolítása',
        detailsAction__nonPrimary: 'Beállítás elsődlegesként',
        detailsAction__primary: 'Visszaigazolás befejezése',
        detailsAction__unverified: 'Visszaigazolás',
        primaryButton: 'Email cím hozzáadása',
        title: 'Email címek',
      },
      enterpriseAccountsSection: {
        primaryButton: 'Fiók összekötése',
        title: 'Vállalati fiókok',
      },
      headerTitle__account: 'Profil adatok',
      headerTitle__security: 'Biztonság',
      mfaSection: {
        backupCodes: {
          actionLabel__regenerate: 'Újra generálás',
          headerTitle: 'Tartalék kódok',
          subtitle__regenerate:
            'Kérj egy új biztonságos tartalék kódokat. Az előző kódok törlésre kerülnek, és nem lesznek használhatók.',
          title__regenerate: 'Tartalék kódok újragenerálása',
        },
        phoneCode: {
          actionLabel__setDefault: 'Beállítás alapértelmezettként',
          destructiveActionLabel: 'Eltávolítás',
        },
        primaryButton: 'Kétlépcsős azonosítás hozzáadása',
        title: 'Két lépcsős azonosítás',
        totp: {
          destructiveActionTitle: 'Eltávolítás',
          headerTitle: 'Hitelesítő alkalmazás',
        },
      },
      passkeysSection: {
        menuAction__destructive: 'Eltávolítás',
        menuAction__rename: 'Átnevezés',
        primaryButton: 'Passkey hozzáadása',
        title: 'Passkey-k',
      },
      passwordSection: {
        primaryButton__setPassword: 'Jelszó beállítása',
        primaryButton__updatePassword: 'Jelszó frissítése',
        title: 'Jelszó',
      },
      phoneNumbersSection: {
        destructiveAction: 'Teleofnszám eltávolítása',
        detailsAction__nonPrimary: 'Beállítás elsődlegesként',
        detailsAction__primary: 'Visszaigazolás befejezése',
        detailsAction__unverified: 'Teleofnszám visszaigazolása',
        primaryButton: 'Telefonszám hozzáadása',
        title: 'Telefonszámok',
      },
      profileSection: {
        primaryButton: 'Profil frissítése',
        title: 'Profil',
      },
      usernameSection: {
        primaryButton__setUsername: 'Felhasználónév beállítása',
        primaryButton__updateUsername: 'Felhasználónév frissítése',
        title: 'Felhasználónév',
      },
      web3WalletsSection: {
        destructiveAction: 'Tárca eltávolítása',
        detailsAction__nonPrimary: 'Beállítás elsődlegesként',
        primaryButton: 'Web3 tárcák',
        title: 'Web3 tárcák',
        web3SelectSolanaWalletScreen: {
          subtitle: 'Válasszon egy Solana tárcát a fiókjához való csatlakozáshoz.',
          title: 'Solana tárca hozzáadása',
        },
      },
    },
    usernamePage: {
      successMessage: 'A felhasználóneved frissítve.',
      title__set: 'Felhasználónév beállítása',
      title__update: 'Felasználónév frissítése',
    },
    web3WalletPage: {
      removeResource: {
        messageLine1: '{{identifier}} el lesz távolítva ebből a fiókból.',
        messageLine2: 'Nem fogod tudni használni a bejelentkezéshez, ezt a web3 tárcát.',
        successMessage: '{{web3Wallet}} eltávolítva a fiókodból.',
        title: 'Web3 tárca eltávolítása',
      },
      subtitle__availableWallets: 'Válaszd ki a web3 tárcát, amit hozzá szeretnél adni a fiókodhoz.',
      subtitle__unavailableWallets: 'Nincs elérhető web3 tárca.',
      successMessage: 'A tárca sikeresen hozzáadva a fiókodhoz.',
      title: 'Web3 tárca hozzáadása',
      web3WalletButtonsBlockButton: '{{provider|titleize}}',
    },
  },
  waitlist: {
    start: {
      actionLink: 'Bejelentkezés',
      actionText: 'Már van hozzáférésed?',
      formButton: 'Csatlakozás a várólistához',
      subtitle: 'Add meg az e-mail címedet, és értesítünk, amikor a helyed elkészül',
      title: 'Csatlakozás a várólistához',
    },
    success: {
      message: 'Hamarosan átirányítunk...',
      subtitle: 'Visszajelzünk, amikor a helyed elkészül',
      title: 'Köszönjük, hogy csatlakoztál a várólistához!',
    },
  },
  web3SolanaWalletButtons: {
    connect: 'Csatlakozás: {{walletName}}',
    continue: 'Folytatás: {{walletName}}',
    noneAvailable:
      'Nem észlelhető Solana Web3 tárca. Kérjük, telepítsen egy Web3-kompatibilis {{ solanaWalletsLink || link("wallet extension") }}-t.',
  },
} as const;
